import React, { useContext, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';

import t from 'react-translate';

import { hasExistingConfiguration, setCurrentConfigurationId } from 'redux/actions/analytics';
import { useAppDispatch } from 'redux/store';
import { getExistingConfiguration } from 'redux/selectors/analytics';
import { AnalyticsPageType, ConfigurationType, ExistingConfiguration } from 'redux/schemas/app/analytics';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { AnalyticsContext } from '../analytics-dashboard';
import NoAccess from './no-access';
import PrePostSurveyReports from './reports';
import PrePostSurveySetupModalContent from './pre-post-survey-setup-modal';
import AnalyticsLoader from '../analytics-loader';
import EmptyReport from './empty-report';
import { config } from '@config/pendo.config.json';

export type PrePostSurveySetupModalProps = {
  onClose?: () => void;
};

const styles = css`
  .reports-container {
    width: 1000px;
  }
  .empty {
    width: 800px;
  }
`;

const PrePostSurveySetupModal: React.FC<PrePostSurveySetupModalProps> = ({ onClose }) => (
  <NvModal
    show
    type={ModalType.DYNAMIC}
    header={t.COURSE_ANALYTICS.PRE_POST.MODAL.TITLE()}
    body={(
      <PrePostSurveySetupModalContent
        onClose={onClose}
      />
    )}
    onClose={onClose}
    width={800}
    closeDataQa={config.pendo.analyticsDashboard.prePost.setupModal.close}
  />
);
export const PrePostSurvey: React.FC = () => {
  const { catalogId, institutionId, analyticsPageType } = useContext(AnalyticsContext);

  const existingConfiguration: ExistingConfiguration = useSelector((state) => getExistingConfiguration(state, analyticsPageType));
  const {
    isLoaded: isExistingConfigurationLoaded,
    id: configurationId, hasAccessToPostCourse,
    postCourseSurvey,
    preCourseSurvey,
    postCourse,
  } = existingConfiguration || {};
  const surveyStatsData = useSelector(state => state.app.analytics.surveyStats?.[configurationId] ?? {});

  const [showPrePostSurveySetupModal, setShowPrePostSurveySetupModal] = useState<boolean>(false);
  const hasConfiguration = configurationId && postCourseSurvey && preCourseSurvey;

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(hasExistingConfiguration({
      institutionId,
      preCourseCatalogId: catalogId,
      configurationType: ConfigurationType[analyticsPageType],
    }));
  }, [analyticsPageType, catalogId, dispatch, institutionId]);

  useEffect(() => {
    if (isExistingConfigurationLoaded && (!hasConfiguration)) {
      setShowPrePostSurveySetupModal(true);
    }
  }, [hasConfiguration, isExistingConfigurationLoaded]);

  useEffect(() => {
    if (configurationId && hasConfiguration) {
      dispatch(setCurrentConfigurationId(configurationId));
    }

    if (!hasConfiguration) {
      dispatch(setCurrentConfigurationId(null));
    }

    return () => {
      dispatch(setCurrentConfigurationId(null));
    };
  }, [configurationId, dispatch, hasConfiguration]);

  if (!isExistingConfigurationLoaded) {
    return (
      <div className='d-flex justify-content-center mx-auto reports-container'>
        <AnalyticsLoader />
      </div>
    );
  }

  if (!hasConfiguration && showPrePostSurveySetupModal) {
    return (
      <PrePostSurveySetupModal onClose={() => setShowPrePostSurveySetupModal(false)} />
    );
  }

  const titleKey = analyticsPageType === AnalyticsPageType.COURSE_ANALYTICS || !configurationId ? 'COURSE' : 'COHORT';

  return (
    <div
      className='d-flex flex-column align-items-center bg-gray-7'
      css={styles}
    >
      <h2 className='course-title-l mt-6'>
        {t.COURSE_ANALYTICS.PRE_POST.PAGE_TITLE[titleKey](surveyStatsData?.totalCourses || 0)}
      </h2>
      {!hasConfiguration && (
        <EmptyReport showSetupModal={() => setShowPrePostSurveySetupModal(true)} />
      )}
      {hasConfiguration && (
        <>
          <h3 className='card-title mt-1'>
            {`${preCourseSurvey?.title} & ${postCourseSurvey?.title}`}
          </h3>
          <ClickableContainer
            className='text-primary align-items-center mt-1'
            onClick={() => setShowPrePostSurveySetupModal(true)}
            aria-labelledby='edit'
            data-qa={config.pendo.analyticsDashboard.prePost.setupModal.open}
          >
            <NvIcon icon='edit' size='xs-smallest' className='mr-1' />
            <span id='edit'>
              {t.COURSE_ANALYTICS.PRE_POST.EDIT()}
            </span>
          </ClickableContainer>
          {!hasAccessToPostCourse && postCourse && (
            <NoAccess postCourseCatalogId={postCourse.catalogId} courseName={postCourse.name} />
          )}
          {hasAccessToPostCourse && (
            <PrePostSurveyReports />
          )}
          {showPrePostSurveySetupModal && (
            <PrePostSurveySetupModal
              onClose={() => setShowPrePostSurveySetupModal(false)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PrePostSurvey;
